import { createFileRoute } from "@tanstack/react-router";
import { InfoPage } from "@/components/InfoPage";

export const Route = createFileRoute("/about")({
  head: () => ({
    meta: [
      { title: "About Picamini.online | Free Classroom & Decision Tools" },
      {
        name: "description",
        content:
          "Learn about Picamini.online, a privacy-focused collection of free classroom, decision-making, and PDF tools for teachers and students.",
      },
      { property: "og:site_name", content: "Picamini.online" },
      { property: "og:title", content: "About Picamini.online | Free Classroom & Decision Tools" },
      { property: "og:description", content: "Learn how Picamini.online creates practical, privacy-focused tools for teachers, students, and facilitators." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://picamini.online/about" },
      { property: "og:image", content: "https://picamini.online/og-image.png" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "About Picamini.online | Free Classroom & Decision Tools" },
      { name: "twitter:description", content: "Practical, privacy-focused classroom and PDF tools for everyday learning moments." },
      { name: "twitter:image", content: "https://picamini.online/og-image.png" },
      { property: "og:locale", content: "en_US" },
      { name: "theme-color", content: "#4F46E5" },
      { name: "robots", content: "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" },
    ],
    links: [
      { rel: "canonical", href: "https://picamini.online/about" },
      { rel: "alternate", hrefLang: "en", href: "https://picamini.online/about" },
      { rel: "alternate", hrefLang: "es", href: "https://picamini.online/es/sobre-nosotros" },
      { rel: "alternate", hrefLang: "de", href: "https://picamini.online/de/ueber-uns" },
      { rel: "alternate", hrefLang: "fr", href: "https://picamini.online/fr/a-propos" },
      { rel: "alternate", hrefLang: "pt-BR", href: "https://picamini.online/pt-br/sobre-o-picamini" },
      { rel: "alternate", hrefLang: "x-default", href: "https://picamini.online/about" },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Home",
              "item": "https://picamini.online/"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "About Us",
              "item": "https://picamini.online/about"
            }
          ]
        })
      }
    ]
  }),
  component: AboutPage,
});

function AboutPage() {
  return (
    <InfoPage
      eyebrow="About Picamini.online"
      title="Practical tools for better learning moments"
      description="Picamini.online creates simple, dependable browser tools that help teachers, students, facilitators, and families make everyday activities easier."
    >
      <h2>What we do</h2>
      <p>
        Picamini.online is an independent educational tools website built around a simple idea:
        useful classroom tools should be quick to open, easy to understand, and respectful of
        user privacy. Our free tools include name pickers, spinner wheels, group generators,
        classroom timers, games, and browser-based PDF utilities.
      </p>
      <p>
        The tools are designed for teachers, schools, tutors, presenters, event hosts, and anyone
        who needs a fair and engaging way to organize a group or complete a common document task.
        We focus on clear interfaces, accessible controls, and practical workflows that work on
        laptops, tablets, phones, and classroom projectors.
      </p>
      <h2>Our approach to privacy</h2>
      <p>
        Many of our classroom tools run entirely in the browser. Names, rosters, wheel entries,
        and other temporary inputs are intended to stay on the user&apos;s device rather than being
        uploaded to a Picamini.online server. Please review our{" "}
        <a href="/privacy-policy">Privacy Policy</a> for the full details, including information
        about cookies and advertising technologies.
      </p>
      <h2>Our commitment to quality</h2>
      <p>
        We aim to keep information accurate, features understandable, and the experience free
        from unnecessary barriers. We continue to improve the site based on real classroom needs.
        Because browser technology and third-party services can change, no website can promise
        that every feature will be available without interruption.
      </p>
      <h2>Get in touch</h2>
      <p>
        Questions, suggestions, and accessibility feedback are welcome. Visit our{" "}
        <a href="/contact">Contact page</a> to reach the Picamini.online team.
      </p>
    </InfoPage>
  );
}
